import Table from '@/common/components/Table'
import { Mapper } from '@/common/services/mapper'
import { cn } from '@/common/utils/cn'
import { useGovernmentAllocationSummary } from '@/features/governmentAllocations/hooks/useGovernmentAllocationSummary'
import {
  governmentAllocationSummaryCurrencyTableColumns,
  governmentAllocationSummaryQuantityTableColumns,
} from '@/features/governmentAllocations/pages/Show/constants/governmentAllocationSummaryTableColumns'
import { GovernmentAllocationSummaryTransformer } from '@/features/governmentAllocations/services/governmentAllocationSummaryTransformer'
import { GovernmentAllocationData } from '@/features/governmentAllocations/types/governmentAllocation.type'
import { GovernmentAllocationSummaryData } from '@/features/governmentAllocations/types/governmentAllocationSummary.type'
import { useEffect, useMemo, useState } from 'react'

type GovernmentAllocationSummaryProps = {
  governmentAllocation: GovernmentAllocationData
}

export const GovernmentAllocationSummary = ({ governmentAllocation }: GovernmentAllocationSummaryProps) => {
  const [categoriesIsOpen, setCategoriesIsOpen] = useState<Record<string, boolean>>({})

 const {
    data: rawSummary,
    isLoading,
  } = useGovernmentAllocationSummary<GovernmentAllocationSummaryData>({
    governmentAllocationId: governmentAllocation.id,
  })

  useEffect(() => {
    if (!rawSummary?.categories) return

    const initialOpen = Object.keys(rawSummary.categories).reduce<Record<string, boolean>>(
      (acc, categoryId) => {
        acc[categoryId] = false
        return acc
      },
      {}
    )

    setCategoriesIsOpen(initialOpen)
  }, [rawSummary?.categories?.[0]?.display_name])

  const handleToggleCategory = (categoryId: string) => {
    setCategoriesIsOpen((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }))
  }

  const rows = useMemo(() => {
    if (!rawSummary) return []

    return Mapper.mapWithTransformer({
      transformer: GovernmentAllocationSummaryTransformer.tableTransformer(
        governmentAllocation.controlled_by,
        categoriesIsOpen,
        handleToggleCategory
      ),
    })(rawSummary)
  }, [rawSummary, categoriesIsOpen])

  return (
    <Table
      className={cn(
        'min-h-[256px]',
        governmentAllocation.controlled_by === 'quantity' ? 'max-h-[calc(100vh-432px)]' : 'max-h-[calc(100vh-496px)]',
      )}
      columns={
        governmentAllocation.controlled_by === 'currency'
          ? governmentAllocationSummaryCurrencyTableColumns
          : governmentAllocationSummaryQuantityTableColumns
      }
      rows={rows || []}
      stripedRows={false}
      page={1}
      totalPages={1}
      isLoadingData={isLoading}
    />
  )
}
